import React, { useEffect, useState } from "react";
import { Save, ShieldCheck } from "lucide-react";
import * as ownerApi from "../../api/payroll/statutoryConfigApi";
import * as portalApi from "../../api/payroll/statutoryConfigPortalApi";
import { Button, Card, CardHeader, CardContent, Input, Checkbox, Alert, Skeleton } from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const empty = {
  pf: { enabled: false, employeePercent: 12, employerPercent: 12, wageCeiling: 15000 },
  esi: { enabled: false, employeePercent: 0.75, employerPercent: 3.25, wageCeiling: 21000 },
  professionalTax: { enabled: false, threshold: 15000, amount: 200, state: "" },
  tds: { enabled: false, annualExemptionLimit: 250000, flatPercent: 0 },
};

export default function StatutoryConfigPage({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const toast = useToast();

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getStatutoryConfig();
        const data = res?.data || res;
        setForm({
          pf: { ...empty.pf, ...(data.pf || {}) },
          esi: { ...empty.esi, ...(data.esi || {}) },
          professionalTax: { ...empty.professionalTax, ...(data.professionalTax || {}) },
          tds: { ...empty.tds, ...(data.tds || {}) },
        });
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [isPortal, api]);

  const setSection = (key, field, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      await api.updateStatutoryConfig(form);
      setMessage("Statutory configuration saved");
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
        <Skeleton height={28} width={240} />
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="space-y-3 p-5">
              <Skeleton height={20} width="40%" />
              <Skeleton height={40} width="100%" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
      <div className="border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
          <ShieldCheck className="h-6 w-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Statutory Configuration
        </h1>
        <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
          Configure PF, ESI, Professional Tax, and TDS rules used during payroll generation
        </p>
      </div>

      
      {message && <Alert variant="success">{message}</Alert>}

      <form onSubmit={save} className="space-y-4">
        {/* PF */}
        <Card>
          <CardHeader className="bg-neutral-50/50">
            <Checkbox
              checked={form.pf.enabled}
              onChange={(e) => setSection("pf", "enabled", e.target.checked)}
              label="Provident Fund (PF)"
              className="text-base font-semibold"
            />
          </CardHeader>
          <CardContent className="p-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Input
                label="Employee %"
                type="number"
                step="0.01"
                value={form.pf.employeePercent}
                onChange={(e) => setSection("pf", "employeePercent", Number(e.target.value))}
              />
              <Input
                label="Employer %"
                type="number"
                step="0.01"
                value={form.pf.employerPercent}
                onChange={(e) => setSection("pf", "employerPercent", Number(e.target.value))}
              />
              <Input
                label="Wage ceiling"
                type="number"
                value={form.pf.wageCeiling}
                onChange={(e) => setSection("pf", "wageCeiling", Number(e.target.value))}
              />
            </div>
          </CardContent>
        </Card>

        {/* ESI */}
        <Card>
          <CardHeader className="bg-neutral-50/50">
            <Checkbox
              checked={form.esi.enabled}
              onChange={(e) => setSection("esi", "enabled", e.target.checked)}
              label="ESI"
              className="text-base font-semibold"
            />
          </CardHeader>
          <CardContent className="p-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Input
                label="Employee %"
                type="number"
                step="0.01"
                value={form.esi.employeePercent}
                onChange={(e) => setSection("esi", "employeePercent", Number(e.target.value))}
              />
              <Input
                label="Employer %"
                type="number"
                step="0.01"
                value={form.esi.employerPercent}
                onChange={(e) => setSection("esi", "employerPercent", Number(e.target.value))}
              />
              <Input
                label="Wage ceiling"
                type="number"
                value={form.esi.wageCeiling}
                onChange={(e) => setSection("esi", "wageCeiling", Number(e.target.value))}
              />
            </div>
          </CardContent>
        </Card>

        {/* PT */}
        <Card>
          <CardHeader className="bg-neutral-50/50">
            <Checkbox
              checked={form.professionalTax.enabled}
              onChange={(e) => setSection("professionalTax", "enabled", e.target.checked)}
              label="Professional Tax"
              className="text-base font-semibold"
            />
          </CardHeader>
          <CardContent className="p-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Input
                label="Threshold"
                type="number"
                value={form.professionalTax.threshold}
                onChange={(e) => setSection("professionalTax", "threshold", Number(e.target.value))}
              />
              <Input
                label="Amount"
                type="number"
                value={form.professionalTax.amount}
                onChange={(e) => setSection("professionalTax", "amount", Number(e.target.value))}
              />
              <Input
                label="State"
                value={form.professionalTax.state}
                onChange={(e) => setSection("professionalTax", "state", e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* TDS */}
        <Card>
          <CardHeader className="bg-neutral-50/50">
            <Checkbox
              checked={form.tds.enabled}
              onChange={(e) => setSection("tds", "enabled", e.target.checked)}
              label="TDS (simplified)"
              className="text-base font-semibold"
            />
          </CardHeader>
          <CardContent className="p-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label="Annual exemption"
                type="number"
                value={form.tds.annualExemptionLimit}
                onChange={(e) => setSection("tds", "annualExemptionLimit", Number(e.target.value))}
              />
              <Input
                label="Flat %"
                type="number"
                step="0.01"
                value={form.tds.flatPercent}
                onChange={(e) => setSection("tds", "flatPercent", Number(e.target.value))}
              />
            </div>
          </CardContent>
        </Card>

        <Button type="submit" variant="primary" isLoading={saving} leftIcon={<Save className="h-4 w-4" />}>
          Save configuration
        </Button>
      </form>
    </div>
  );
}